import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Archive, Search, ChevronLeft, ChevronRight, Clock, RotateCcw, AlertTriangle, FileText, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ArchivePage = () => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [reopenModal, setReopenModal] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/archive`, {
        params: { search, page, limit: 50 },
      });
      setOrders(res.data.orders);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error("Failed to fetch archive:", err);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleReopen = async () => {
    if (!reopenModal || !reopenReason.trim()) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/orders/${reopenModal.id}/reopen`, {
        destination: "to_dispatch",
        reopen_reason: reopenReason.trim(),
      });
      setMessage({ type: "success", text: `Order ${reopenModal.order_number} reopened and sent to Dispatch` });
      setReopenModal(null);
      setReopenReason("");
      fetchOrders();
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Reopen failed" });
    } finally {
      setProcessing(false);
    }
  };

  const reasonBadge = (reason) => {
    if (!reason) return <span className="text-gray-400 text-xs">-</span>;
    if (reason === "Removed by Safeguard" || reason === "Removed from Source") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#C07070]/12 text-[#8B4545] border border-[#C07070]/25">
          <AlertTriangle className="h-3 w-3" /> Removed from Source
        </span>
      );
    }
    if (reason === "Cancelled (Safeguard)") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#C07070]/12 text-[#8B4545] border border-[#C07070]/25">
          <X className="h-3 w-3" /> Cancelled
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-600">
        {reason}
      </span>
    );
  };

  return (
    <div className="space-y-5" data-testid="archive-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Archive className="h-5 w-5 text-gray-500" />
          <h1 className="text-xl font-bold text-gray-800" data-testid="archive-title">Archive</h1>
          <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 border border-gray-300" data-testid="archive-total-badge">
            {total}
          </span>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search order, client, city, address..."
              className="pl-8 pr-3 py-2 w-72 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-[#D1A054]/50 transition-colors"
              data-testid="archive-search-input"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-[#B08A3E] hover:bg-[#9A7A3E] text-white text-sm font-medium rounded-lg transition-colors"
            data-testid="archive-search-btn"
          >
            Search
          </button>
        </form>
      </div>

      {message && (
        <div className={`p-3 rounded-lg flex items-center justify-between text-sm ${message.type === "success" ? "bg-[#A7C4A0]/15 border border-[#A7C4A0]/40 text-[#6B8F62]" : "bg-[#C07070]/12 border border-[#C07070]/30 text-[#8B4545]"}`} data-testid="archive-message">
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200" data-testid="archive-table-container">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#D1A054]" />
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">{search ? "No results found" : "No archived orders"}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="archive-table">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500">
                    <th className="text-left px-3 py-2.5 font-medium text-xs">Order #</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">Company</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">Client</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">PH Name</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">City</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">State</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">Reason</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">Archived</th>
                    <th className="text-left px-3 py-2.5 font-medium text-xs">Events</th>
                    <th className="text-right px-3 py-2.5 font-medium text-xs w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        data-testid={`archive-row-${order.id}`}
                        onClick={() => setExpandedRow(expandedRow === order.id ? null : order.id)}
                      >
                        <td className="px-3 py-2.5 text-gray-800 font-medium whitespace-nowrap">{order.order_number || "-"}</td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.company || "-"}</td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.cliente || "-"}</td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.ph_name || "-"}</td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.city || "-"}</td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{order.state || "-"}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{reasonBadge(order.archived_reason)}</td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                          {order.archived_at ? new Date(order.archived_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {order.event_history?.length > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-[#B08A3E] font-medium">
                              <Clock className="h-3 w-3" /> {order.event_history.length}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); setReopenModal(order); setReopenReason(""); }}
                            className="px-2.5 py-1 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-[10px] font-bold rounded-md flex items-center gap-1 ml-auto transition-colors"
                            data-testid={`reopen-btn-${order.id}`}
                          >
                            <RotateCcw className="h-3 w-3" /> Reopen
                          </button>
                        </td>
                      </tr>
                      {expandedRow === order.id && order.event_history?.length > 0 && (
                        <tr data-testid={`archive-events-${order.id}`}>
                          <td colSpan={10} className="px-3 py-3 bg-gray-50/60">
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Event History</p>
                              {[...order.event_history].reverse().map((ev, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs">
                                  <EventIcon event={ev.event} />
                                  <div>
                                    <span className="text-gray-800 font-medium">{eventLabel(ev.event)}</span>
                                    <span className="text-gray-500 ml-1.5">{ev.detail}</span>
                                    <span className="text-gray-300 ml-2">
                                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Page {page} of {pages} ({total} orders)
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    data-testid="archive-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pages, page + 1))}
                    disabled={page >= pages}
                    className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    data-testid="archive-next-page"
                  >
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reopen Confirmation Modal */}
      {reopenModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="reopen-modal">
          <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-[#2F5D8A]/10 rounded-lg">
                <RotateCcw className="h-5 w-5 text-[#2F5D8A]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1F2937]">Reopen Order</h3>
                <p className="text-xs text-gray-500">Order {reopenModal.order_number} will be sent back to Dispatch</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-400 font-medium">Company:</span> <span className="text-gray-700">{reopenModal.company}</span></div>
                <div><span className="text-gray-400 font-medium">Client:</span> <span className="text-gray-700">{reopenModal.cliente || "-"}</span></div>
                <div><span className="text-gray-400 font-medium">City:</span> <span className="text-gray-700">{reopenModal.city}</span></div>
                <div><span className="text-gray-400 font-medium">Reason:</span> <span className="text-gray-700">{reopenModal.archived_reason || "-"}</span></div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Reopen Reason *
              </label>
              <textarea
                value={reopenReason}
                onChange={e => setReopenReason(e.target.value)}
                placeholder="Why is this order being reopened..."
                rows={3}
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#2F5D8A] focus:outline-none resize-none"
                data-testid="reopen-reason-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setReopenModal(null); setReopenReason(""); }}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors"
                data-testid="reopen-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleReopen}
                disabled={processing || !reopenReason.trim()}
                className="flex-1 px-4 py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
                data-testid="reopen-confirm-btn"
              >
                <Send className="h-4 w-4" />
                {processing ? "Reopening..." : "Reopen to Dispatch"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EventIcon = ({ event }) => {
  if (event === "removed_by_safeguard" || event === "removed_from_source") return <AlertTriangle className="h-3.5 w-3.5 text-[#8B4545] mt-0.5 shrink-0" />;
  if (event === "returned_reappeared") return <RotateCcw className="h-3.5 w-3.5 text-[#6B8F62] mt-0.5 shrink-0" />;
  if (event === "duplicate_detected") return <FileText className="h-3.5 w-3.5 text-[#B08A3E] mt-0.5 shrink-0" />;
  return <Clock className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />;
};

const eventLabel = (event) => {
  const labels = {
    removed_by_safeguard: "Removed from Source",
    removed_from_source: "Removed from Source",
    returned_reappeared: "Returned / Reappeared",
    duplicate_detected: "Duplicate Detected",
  };
  return labels[event] || event;
};

export default ArchivePage;
